import type { NavItem } from '@/shared/registry'

/** Where the course catalog lives, and each course's learner page; both open signed out when the site allows it. */
export const courseCatalogPath = '/catalog/course-catalog'
export const learnerCourseRoute = `${courseCatalogPath}/:publicId`

/** The catalog of one organization, or the viewer's own when none is named. */
export function courseCatalogOf(organizationId?: string | null): string {
  return organizationId === undefined || organizationId === null || organizationId === ''
    ? courseCatalogPath
    : `${courseCatalogPath}?${new URLSearchParams({ organization: organizationId }).toString()}`
}

/** A course's learner page, opened from the catalog of `organizationId` when given. */
export function learnerCoursePath(id: string, organizationId?: string | null): string {
  const path = `${courseCatalogPath}/${encodeURIComponent(id)}`
  return organizationId === undefined || organizationId === null || organizationId === ''
    ? path
    : `${path}?${new URLSearchParams({ organization: organizationId }).toString()}`
}

/** Browse the course catalog and open the courses published to the viewer. */
export const readCourseCatalogPermission = 'catalog.course-catalog.read'

/** "Courses" in the nav, for whoever browses the catalog (CAT-29), while it is on for them. */
export const courseCatalogNav: NavItem = { id: 'course-catalog', labelId: 'catalog.courseCatalog.nav', path: courseCatalogPath, permission: readCourseCatalogPermission }

/** The API's catalog endpoints. */
export const courseCatalogApi = '/catalog/v1/course-catalog'

/** The viewer's own catalog's heading; 404 while the site's or their organization's catalog is off. */
export const courseCatalogAboutApi = `${courseCatalogApi}/about`

/** Whether "Courses" is in the viewer's menu; off is a 200 answer, not a 404. */
export const courseCatalogVisibilityApi = `${courseCatalogApi}/visibility`

/** Whether the visibility answer says Courses shows; anything else hides it. */
export function isCourseCatalogVisible(body: unknown): boolean {
  return typeof body === 'object' && body !== null && 'isVisible' in body && body.isVisible === true
}

export function learnerCourseApi(id: string): string {
  return `${courseCatalogApi}/courses/${encodeURIComponent(id)}`
}

/**
 * The slots the learner's pages offer other areas. On the catalog's cards and list:
 * `progress`, a column of each course's progress, which Learner History fills. On a course's
 * learner page, besides its `details` slots (tabs, side-menu links, header actions, panels):
 * `actions` beside the course's card, such as Entitlements' self-assign button (ENT-13);
 * `overview`, panels at the end of Overview, such as Classes' upcoming classes (CLS-05);
 * `activities`, panels after the activity list, which Launch fills; `resources`, panels at the
 * top of Resources, such as Launch's Course Resources (LCH-27); and `resourcesEnd`, panels after
 * the presenters, such as Catalog's "Help us improve".
 */
export const courseCatalogSlots = {
  progress: 'catalog.course-catalog.progress',
  details: 'catalog.learner-course',
  actions: 'catalog.learner-course.card-actions',
  overview: 'catalog.learner-course.overview',
  activities: 'catalog.learner-course.activities',
  resources: 'catalog.learner-course.resources',
  resourcesEnd: 'catalog.learner-course.resources-end',
} as const
